import { toast } from 'sonner';
import { authFetch } from '@/lib/auth-fetch';

const TOO_MANY_REQUESTS = 429;
const MAX_RATE_LIMIT_RETRIES = 3;
const BASE_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30_000;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Parses the Retry-After header (delta-seconds) into milliseconds.
 * Returns undefined when the header is absent or unparseable.
 */
export function parseRetryAfterMs(response: Response): number | undefined {
  const header = response.headers.get('Retry-After');
  if (!header) {
    return undefined;
  }
  const seconds = Number(header);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return seconds * 1000;
  }
  return undefined;
}

/**
 * Wraps authFetch to transparently retry HTTP 429 responses using exponential
 * backoff, honoring the server's Retry-After header when present. When retries
 * are exhausted the final 429 response is returned so the caller surfaces an ApiError.
 */
export async function fetchWithRateLimitRetry(
  url: string,
  init: RequestInit,
  signal?: AbortSignal,
): Promise<Response> {
  let attempt = 0;

  while (true) {
    const response = await authFetch(url, init, signal);

    if (response.status !== TOO_MANY_REQUESTS || attempt >= MAX_RATE_LIMIT_RETRIES) {
      if (response.status === TOO_MANY_REQUESTS) {
        toast.error('Too many requests. Please slow down and try again in a moment.');
      }
      return response;
    }

    const retryAfterMs = parseRetryAfterMs(response);
    const backoffMs = Math.min(BASE_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS);
    const delayMs = Math.max(retryAfterMs ?? 0, backoffMs);

    attempt += 1;
    await sleep(delayMs);

    if (signal?.aborted) {
      return response;
    }
  }
}
